import type { GetStripePriceCatalogPriceResponseBody } from '@calm-web/api-sdk/lib/app-api';

import { Coupon, Prices } from '@/store/prices/types';
import { PlanCadence } from '@/store/purchaseParams/types';

import { STRIPE_ZERO_DECIMAL_CURRENCIES } from './constants';

export type PriceCatalogCoupon = Omit<Coupon, 'valid'> & {
	valid?: boolean;
	applies_to_products?: string[];
};

type CatalogBody = GetStripePriceCatalogPriceResponseBody;

// The api sends `coupon` but its response spec does not declare it yet.
export type PriceCatalogResponse = Pick<CatalogBody, 'currency' | 'price'> &
	Partial<Pick<CatalogBody, 'original_price' | 'original_tiered_price'>> & {
		coupon?: PriceCatalogCoupon | null;
	};

// The api only returns a coupon it found valid, and omits `valid` when it is true.
const toCoupon = ({ applies_to_products, valid = true, ...coupon }: PriceCatalogCoupon): Coupon => ({
	...coupon,
	valid,
});

export const mergeCatalogPriceIntoPrices = (
	priceCatalog: PriceCatalogResponse,
	prices: Prices | null,
	priceKey: PlanCadence,
) => {
	// A falsy `prices` means the sub-prices fetch itself returned an empty body — nothing to
	// merge into, so degrade to the fetch's own null rather than crash on `prices.current`.
	if (!prices) {
		return null;
	}

	// no valid family plan price if the currency doesn't match
	if (priceCatalog.currency.toLowerCase() !== prices.current.currency.toLowerCase()) {
		return prices;
	}

	const toMinorUnits = (amount: number) =>
		STRIPE_ZERO_DECIMAL_CURRENCIES.has(priceCatalog.currency.toLowerCase()) ? amount * 100 : amount;

	// The catalog discounts by any coupon, but family checkout refuses one that is not scoped to
	// the family product. An unscoped coupon is refused, and so is one the sub-prices response files
	// under an individual plan, because that is scoped to an individual product.
	const catalogCoupon = priceCatalog.coupon;
	const isIndividualCoupon =
		!!catalogCoupon && Object.values(prices.coupons ?? {}).some(c => c?.id === catalogCoupon.id);
	const scopedCoupon =
		catalogCoupon?.applies_to_products?.length && !isIndividualCoupon ? catalogCoupon : null;
	const listPrice = toMinorUnits(
		priceCatalog.original_tiered_price ?? priceCatalog.original_price ?? priceCatalog.price,
	);
	const price = scopedCoupon ? toMinorUnits(priceCatalog.price) : listPrice;

	return {
		...prices,
		original: {
			...prices.original,
			[priceKey]: listPrice,
		},
		current: {
			...prices.current,
			[priceKey]: price,
		},
		...(scopedCoupon && {
			coupons: {
				...prices.coupons,
				[priceKey]: toCoupon(scopedCoupon),
			},
		}),
	};
};
